import type {
  ProjectMemoryList,
  ProjectMemoryStatus,
  ProjectMemoryView,
  ProjectView,
} from "@esi/shared";
import {
  Check,
  Ellipsis,
  EyeOff,
  FileText,
  Lock,
  Pencil,
  Search,
  Trash2,
  TriangleAlert,
  Undo2,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { FilterChips } from "@/components/FilterChips";
import { InlineError, Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { ApiError } from "../api-errors.ts";
import { formatDate } from "../i18n/format.ts";
import { can } from "../projects/capabilities.ts";
import { MemoryForm } from "./MemorySection.tsx";
import { useProjectMemories, useProjectMemoryMutations } from "./queries.ts";

/** Loosely typed `t` for the helpers below (the full TFunction type is too deep to infer). */
type T = (key: string, options?: Record<string, unknown>) => string;
type ProjectRef = Pick<ProjectView, "id" | "capabilities">;

/** A failed write in plain words: the server's refusal, or the network. */
function failure(error: unknown, t: T): string {
  if (error instanceof ApiError && error.status === 422) {
    return t("memory.proposal.invalid");
  }
  if (error instanceof ApiError && error.status === 404) {
    return t("memory.project.gone");
  }
  return error instanceof ApiError ? error.message : t("errors.network");
}

/**
 * A project's memory tab (openspec change `memory-project`, design §7): the facts the team
 * agreed on, searchable, and for owners and managers the review queue with approve, edit and
 * reject. Contributors see and withdraw their own suggestions. Who suggested and approved a fact
 * is shown to owners and managers only; a fact whose sources the viewer cannot read is a hidden
 * row. The queue sits in a dashed container: in this design system dashed means "not yet".
 */
export function ProjectMemory(props: { project: ProjectRef }) {
  const { project } = props;
  const { t } = useTranslation();
  const tt = t as unknown as T;
  const manages = can(project, "memory.manage");
  const proposes = manages || can(project, "memory.propose");
  const [status, setStatus] = useState<ProjectMemoryStatus>("active");
  const [query, setQuery] = useState("");
  const [settled, setSettled] = useState("");
  const [announce, setAnnounce] = useState<string | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(query), 250);
    return () => clearTimeout(timer);
  }, [query]);
  const shownStatus = proposes ? status : "active";
  const active = useProjectMemories(project.id, "active", shownStatus === "active" ? settled : "");
  // Readers have no queue: the server would refuse it.
  const pending = useProjectMemories(
    project.id,
    "pending",
    shownStatus === "pending" ? settled : "",
    { enabled: proposes },
  );
  const current = shownStatus === "active" ? active : pending;
  const lastData = useRef<ProjectMemoryList | null>(null);
  const data = current.data ?? lastData.current;
  if (current.data) {
    lastData.current = current.data;
  }
  if (current.isError && !data) {
    return <Notice tone="danger">{failure(current.error, tt)}</Notice>;
  }
  if (!data || !active.data) {
    return <Skeleton className="h-40 w-full" />;
  }
  const pendingCount = active.data.pendingCount;

  return (
    <div data-esi-content="memory" className="mx-auto flex w-full max-w-3xl flex-col gap-4">
      <p className="max-w-prose text-sm leading-relaxed text-fg-secondary">
        {manages ? t("memory.project.leadManager") : t("memory.project.lead")}
      </p>
      {active.data.enabled ? null : <Notice>{t("memory.project.off")}</Notice>}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {proposes ? (
          <FilterChips
            label={t("memory.project.filter")}
            value={shownStatus}
            onChange={(next) => {
              setStatus(next);
              setQuery("");
              setSettled("");
            }}
            options={[
              {
                value: "active",
                label: t("memory.project.agreed"),
                count: settled && shownStatus === "active" ? undefined : active.data.items.length,
              },
              {
                value: "pending",
                label: manages ? t("memory.project.queue") : t("memory.project.mine"),
                count: pendingCount,
              },
            ]}
          />
        ) : (
          <span />
        )}
        <div className="relative w-full sm:max-w-60">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-fg-muted"
            strokeWidth={1.75}
          />
          <Input
            type="search"
            aria-label={t("memory.project.search")}
            placeholder={t("memory.project.search")}
            value={query}
            maxLength={200}
            onChange={(e) => setQuery(e.currentTarget.value)}
            className="h-8 pl-8 text-[13px]"
          />
        </div>
      </div>
      {current.isError ? <InlineError>{failure(current.error, tt)}</InlineError> : null}
      {data.items.length === 0 ? (
        <p
          className={cn(
            "rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-fg-muted",
          )}
        >
          {query.trim()
            ? t("memory.project.noMatch")
            : shownStatus === "pending"
              ? manages
                ? t("memory.project.emptyQueue")
                : t("memory.project.emptyMine")
              : t("memory.project.empty")}
        </p>
      ) : (
        <ul
          aria-label={
            shownStatus === "pending"
              ? manages
                ? t("memory.project.queue")
                : t("memory.project.mine")
              : t("memory.project.agreed")
          }
          className={cn(
            "flex flex-col divide-y divide-line rounded-lg border bg-surface-raised",
            shownStatus === "pending" ? "border-dashed border-line-strong" : "border-line",
          )}
        >
          {data.items.map((memory) =>
            memory.status === "pending" ? (
              <PendingRow
                key={memory.id}
                project={project}
                memory={memory}
                manages={manages}
                onDone={setAnnounce}
              />
            ) : (
              <ActiveRow
                key={memory.id}
                project={project}
                memory={memory}
                manages={manages}
                onDone={setAnnounce}
              />
            ),
          )}
        </ul>
      )}
      <p role="status" className="sr-only">
        {announce}
      </p>
    </div>
  );
}

/** The 12px muted meta line, items joined by middots. */
function Meta(props: { items: ReactNode[] }) {
  const items = props.items.filter(Boolean);
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-fg-muted">
      {items.map((item, i) => (
        // Positions are stable: the list is rebuilt from the same fields each render. The
        // middot trails its item, so a wrapped line never starts with one.
        // biome-ignore lint/suspicious/noArrayIndexKey: see above.
        <span key={i} className="inline-flex items-center gap-1.5">
          {item}
          {i < items.length - 1 ? <span aria-hidden="true">·</span> : null}
        </span>
      ))}
    </p>
  );
}

/** Sources, unverified trust and, for managers, how many members cannot use it. */
function trustItems(memory: ProjectMemoryView, t: T): ReactNode[] {
  return [
    memory.sourceCount > 0 ? (
      <span key="sources" className="inline-flex items-center gap-1">
        <FileText aria-hidden="true" className="size-3" strokeWidth={1.75} />
        {t("memory.proposal.sources", { count: memory.sourceCount })}
      </span>
    ) : null,
    memory.trust === "external" ? (
      <span key="trust" className="inline-flex items-center gap-1 text-warning-fg">
        <TriangleAlert aria-hidden="true" className="size-3" strokeWidth={1.75} />
        {t("memory.proposal.unverified")}
      </span>
    ) : null,
    memory.membersWithoutAccess ? (
      <span key="restricted" className="inline-flex items-center gap-1 tabular-nums">
        <Lock aria-hidden="true" className="size-3" strokeWidth={1.75} />
        {t("memory.project.restricted", { count: memory.membersWithoutAccess })}
      </span>
    ) : null,
  ];
}

const person = (p: { name: string | null } | null, t: T) => p?.name ?? t("memory.project.someone");

/** A fact the viewer cannot read the sources of: no text, and only the manager's way out. */
function HiddenRow(props: {
  memory: ProjectMemoryView;
  action: { label: string; aria: string; busy: boolean; run: () => void } | null;
  error: unknown;
}) {
  const { t } = useTranslation();
  const tt = t as unknown as T;
  return (
    <li className="flex flex-col gap-2 px-3.5 py-3">
      <div className="flex items-center gap-3">
        <EyeOff aria-hidden="true" className="size-4 shrink-0 text-fg-muted" strokeWidth={1.75} />
        <p className="min-w-0 flex-1 text-sm text-fg-secondary">{t("memory.project.hidden")}</p>
        {props.action ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={props.action.busy}
            onClick={props.action.run}
            aria-label={props.action.aria}
          >
            {props.action.label}
          </Button>
        ) : null}
      </div>
      {props.error ? <InlineError>{failure(props.error, tt)}</InlineError> : null}
    </li>
  );
}

function ActiveRow(props: {
  project: ProjectRef;
  memory: ProjectMemoryView;
  manages: boolean;
  onDone: (message: string) => void;
}) {
  const { memory, manages } = props;
  const { t } = useTranslation();
  const tt = t as unknown as T;
  const id = useId();
  const { edit, remove } = useProjectMemoryMutations(props.project.id);
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const deleteIt = () =>
    // Awaited, not `mutate` callbacks: the row unmounts when the list refreshes.
    remove.mutateAsync(memory.id).then(
      () => {
        setConfirming(false);
        props.onDone(t("memory.project.deleted"));
      },
      () => {},
    );

  const dialog = (
    <AppDialog
      open={confirming}
      onClose={() => setConfirming(false)}
      title={t("memory.project.deleteTitle")}
      returnFocus={trigger.current}
    >
      <ul className="flex list-disc flex-col gap-1.5 pl-5 text-sm text-fg-secondary">
        <li>{t("memory.project.deleteEffects.everyone")}</li>
        <li>{t("memory.project.deleteEffects.audited")}</li>
      </ul>
      {remove.error ? <InlineError>{failure(remove.error, tt)}</InlineError> : null}
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={() => setConfirming(false)}>
          {t("memory.proposal.cancel")}
        </Button>
        <Button type="button" variant="destructive" disabled={remove.isPending} onClick={deleteIt}>
          {remove.isPending ? t("memory.panel.deleting") : t("memory.project.confirmDelete")}
        </Button>
      </DialogFooter>
    </AppDialog>
  );

  if (memory.hidden) {
    return (
      <>
        <HiddenRow
          memory={memory}
          error={remove.error}
          action={
            manages
              ? {
                  label: t("memory.panel.delete"),
                  aria: t("memory.project.deleteHidden"),
                  busy: remove.isPending,
                  run: () => {
                    remove.reset();
                    setConfirming(true);
                  },
                }
              : null
          }
        />
        {dialog}
      </>
    );
  }

  return (
    <li className="flex flex-col gap-2 px-3.5 py-3">
      {editing ? (
        <MemoryForm
          id={id}
          initialKind="fact"
          initialText={memory.text ?? ""}
          withKind={false}
          busy={edit.isPending}
          error={edit.error ? failure(edit.error, tt) : null}
          onCancel={() => {
            edit.reset();
            setEditing(false);
            trigger.current?.focus();
          }}
          onSubmit={({ text }) =>
            edit.mutate(
              { id: memory.id, text },
              {
                onSuccess: () => {
                  setEditing(false);
                  props.onDone(t("memory.panel.saved"));
                },
              },
            )
          }
        />
      ) : (
        <div className="flex items-start gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <p className="text-sm leading-relaxed break-words text-fg">{memory.text}</p>
            <Meta
              items={[
                manages && memory.author ? (
                  <span key="people">
                    {memory.decidedBy && memory.decidedBy.id !== memory.author.id
                      ? t("memory.project.suggestedApproved", {
                          author: person(memory.author, tt),
                          decider: person(memory.decidedBy, tt),
                        })
                      : t("memory.project.addedBy", { author: person(memory.author, tt) })}
                  </span>
                ) : null,
                ...trustItems(memory, tt),
                <span key="used" className="tabular-nums">
                  {memory.lastUsedAt
                    ? t("memory.panel.lastUsed", { date: formatDate(memory.lastUsedAt) })
                    : t("memory.panel.neverUsed")}
                </span>,
              ]}
            />
          </div>
          {manages ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  ref={trigger}
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t("memory.panel.actions")}
                  className="-my-1 shrink-0 text-fg-muted hover:text-fg"
                >
                  <Ellipsis aria-hidden="true" strokeWidth={1.75} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-40">
                <DropdownMenuItem
                  onSelect={() => {
                    edit.reset();
                    setEditing(true);
                  }}
                >
                  <Pencil aria-hidden="true" strokeWidth={1.75} />
                  {t("memory.panel.edit")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => {
                    remove.reset();
                    setConfirming(true);
                  }}
                >
                  <Trash2 aria-hidden="true" strokeWidth={1.75} />
                  {t("memory.panel.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      )}
      {dialog}
    </li>
  );
}

function PendingRow(props: {
  project: ProjectRef;
  memory: ProjectMemoryView;
  manages: boolean;
  onDone: (message: string) => void;
}) {
  const { memory, manages } = props;
  const { t } = useTranslation();
  const tt = t as unknown as T;
  const id = useId();
  const { decide, withdraw } = useProjectMemoryMutations(props.project.id);
  const [editing, setEditing] = useState(false);
  const busy = decide.isPending || withdraw.isPending;
  const error = decide.error ?? withdraw.error;
  const reject = () =>
    decide.mutateAsync({ id: memory.id, action: "reject" }).then(
      () => props.onDone(t("memory.project.rejected")),
      () => {},
    );

  if (memory.hidden) {
    return (
      <HiddenRow
        memory={memory}
        error={error}
        action={
          manages
            ? {
                label: t("memory.project.reject"),
                aria: t("memory.project.rejectHidden"),
                busy,
                run: reject,
              }
            : null
        }
      />
    );
  }

  return (
    <li className="flex flex-col gap-2.5 px-3.5 py-3">
      {editing ? (
        <MemoryForm
          id={id}
          initialKind="fact"
          initialText={memory.text ?? ""}
          withKind={false}
          busy={decide.isPending}
          error={decide.error ? failure(decide.error, tt) : null}
          submitLabel={t("memory.project.approve")}
          onCancel={() => {
            decide.reset();
            setEditing(false);
          }}
          onSubmit={({ text }) =>
            decide
              .mutateAsync({
                id: memory.id,
                action: "approve",
                ...(text !== memory.text && { text }),
              })
              .then(
                () => props.onDone(t("memory.project.approved")),
                () => {},
              )
          }
        />
      ) : (
        <>
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-sm leading-relaxed break-words text-fg">{memory.text}</p>
            <Meta
              items={[
                manages && memory.author ? (
                  <span key="people">
                    {t("memory.project.suggestedBy", { author: person(memory.author, tt) })}
                  </span>
                ) : memory.mine ? (
                  <span key="mine">{t("memory.project.yourSuggestion")}</span>
                ) : null,
                ...trustItems(memory, tt),
                memory.expiresAt ? (
                  <span key="expires" className="tabular-nums">
                    {t("memory.project.expires", { date: formatDate(memory.expiresAt) })}
                  </span>
                ) : null,
              ]}
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {manages ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  disabled={busy}
                  onClick={() =>
                    decide.mutateAsync({ id: memory.id, action: "approve" }).then(
                      () => props.onDone(t("memory.project.approved")),
                      () => {},
                    )
                  }
                >
                  <Check aria-hidden="true" strokeWidth={1.75} />
                  {t("memory.project.approve")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    decide.reset();
                    setEditing(true);
                  }}
                >
                  <Pencil aria-hidden="true" strokeWidth={1.75} />
                  {t("memory.project.editApprove")}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  className="text-fg-secondary"
                  onClick={reject}
                >
                  <X aria-hidden="true" strokeWidth={1.75} />
                  {t("memory.project.reject")}
                </Button>
              </>
            ) : null}
            {memory.mine && !manages ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                className="-ml-2 text-fg-secondary"
                onClick={() =>
                  withdraw.mutateAsync(memory.id).then(
                    () => props.onDone(t("memory.project.withdrawn")),
                    () => {},
                  )
                }
              >
                <Undo2 aria-hidden="true" strokeWidth={1.75} />
                {t("memory.project.withdraw")}
              </Button>
            ) : null}
          </div>
        </>
      )}
      {error && !editing ? <InlineError>{failure(error, tt)}</InlineError> : null}
    </li>
  );
}
